"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateRealtimeScoringDefinition = void 0;
const metric_catalog_1 = require("./metric-catalog");
const formula_resolution_1 = require("./formula-resolution");
// Callers that have not established an owning client must not be able to use
// client-scoped metrics. Explicitly opt in once the owner has been resolved.
const defaultValidationContext = { hasClientScope: false };
const isAvailableForValidation = (metric, context) => (0, metric_catalog_1.isAuthorableRealtimeMetric)(metric) ||
    (context.allowDeferredMetricReferences === true && metric.availability === 'deferred');
/** Keeps compilation of a user-authored expression predictably bounded. */
const MAX_CONDITION_DEPTH = 8;
const MAX_PREDICATES_PER_CONDITION = 25;
const MAX_SCORE_RULES = 100;
const RATIO_OPERATORS = new Set(['eq', 'neq', 'gt', 'gte', 'lt', 'lte']);
const isMoneyValue = (value) => typeof value === 'object' && !Array.isArray(value) && 'amount' in value && 'currency' in value;
const isIsoDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
};
const isValueCompatible = (valueType, operator, value) => {
    if (operator === 'in' || operator === 'notIn') {
        return (Array.isArray(value) &&
            value.length > 0 &&
            value.every((entry) => valueType === 'date' ? typeof entry === 'string' && isIsoDate(entry) : typeof entry === valueType));
    }
    switch (valueType) {
        case 'number':
            return typeof value === 'number' && Number.isFinite(value);
        case 'boolean':
            return typeof value === 'boolean';
        case 'string':
            return typeof value === 'string';
        case 'string_array':
            // Array-valued source facts are searched one element at a time. The
            // predicate is the string to look for, never a caller-supplied array
            // expression.
            return typeof value === 'string';
        case 'number_array':
            // Numeric array facts are queried element by element, just like string
            // arrays, but their predicate must remain a finite scalar number.
            return typeof value === 'number' && Number.isFinite(value);
        case 'date':
            return typeof value === 'string' && isIsoDate(value);
        case 'money':
            return isMoneyValue(value) && Number.isFinite(value.amount) && /^[A-Z]{3}$/.test(value.currency);
        default:
            return false;
    }
};
const isSniCode = (value) => /^\d{2,5}$/.test(value);
const isIso3166Alpha2 = (value) => /^[A-Z]{2}$/.test(value);
/**
 * A literal trigram term avoids wildcard scans for source-backed text fields.
 * The catalog intentionally keeps legacy pattern semantics available only for
 * older metrics that do not opt in to this policy.
 */
const isSelectiveLiteralTrigramSearch = (value) => typeof value === 'string' && !/[\\%_]/.test(value) && Array.from(value.trim()).length >= 3;
const validateMetricScope = (metric, path, context, issues) => {
    if (metric.dataScope === 'client' && !context.hasClientScope) {
        issues.push({ path, message: `The metric requires a client-owned filter: ${metric.key}` });
    }
};
const validateLocalMetricQueryPhase = (localMetric, path, queryPhase, issues) => {
    for (const metricKey of localMetric.descriptor.referencedMetricKeys) {
        const metric = (0, metric_catalog_1.findRealtimeMetric)(metricKey);
        if (metric !== undefined && !metric.queryPhases.includes(queryPhase)) {
            issues.push({
                path,
                message: `The local formula cannot be used for ${queryPhase}: ${metricKey}`,
            });
        }
    }
};
const hasValidSniValue = (value) => typeof value === 'string'
    ? isSniCode(value)
    : Array.isArray(value) && value.every((entry) => typeof entry === 'string' && isSniCode(entry));
const hasValidIso3166Alpha2Value = (value) => typeof value === 'string'
    ? isIso3166Alpha2(value)
    : Array.isArray(value) && value.every((entry) => typeof entry === 'string' && isIso3166Alpha2(entry));
const getConditionComplexity = (condition, depth = 1) => {
    switch (condition.kind) {
        case 'predicate':
        case 'ratio':
            return { depth, predicateCount: 1 };
        case 'not':
            return getConditionComplexity(condition.condition, depth + 1);
        case 'all':
        case 'any': {
            const children = condition.conditions.map((child) => getConditionComplexity(child, depth + 1));
            return {
                depth: Math.max(depth, ...children.map((child) => child.depth)),
                predicateCount: children.reduce((count, child) => count + child.predicateCount, 0),
            };
        }
    }
};
const validateNumericRatioMetric = (metricKey, path, queryPhase, context, localMetrics, issues) => {
    const localMetric = (0, formula_resolution_1.findResolvedLocalMetric)(localMetrics, metricKey);
    const metric = localMetric?.definition ?? (0, metric_catalog_1.findRealtimeMetric)(metricKey);
    if (metric === undefined) {
        issues.push({ path, message: `Unknown metric: ${metricKey}` });
        return;
    }
    if (localMetric === undefined && !isAvailableForValidation(metric, context)) {
        issues.push({ path, message: `The metric is not available yet: ${metricKey}` });
    }
    validateMetricScope(metric, path, context, issues);
    if (localMetric !== undefined) {
        validateLocalMetricQueryPhase(localMetric, path, queryPhase, issues);
    }
    if (!metric.queryPhases.includes(queryPhase)) {
        issues.push({
            path,
            message: `The metric cannot be used for ${queryPhase === 'eligibility' ? 'eligibility' : 'scoring'}: ${metricKey}`,
        });
    }
    if (metric.valueType !== 'number') {
        issues.push({ path, message: `A ratio requires a numeric metric: ${metricKey}` });
    }
};
const validateRatioCondition = (condition, path, queryPhase, context, localMetrics, seenRatioIds, issues) => {
    if (seenRatioIds.has(condition.id)) {
        issues.push({ path: `${path}.id`, message: `The ratio id must be unique: ${condition.id}` });
    }
    seenRatioIds.add(condition.id);
    validateNumericRatioMetric(condition.numeratorMetric, `${path}.numeratorMetric`, queryPhase, context, localMetrics, issues);
    validateNumericRatioMetric(condition.denominatorMetric, `${path}.denominatorMetric`, queryPhase, context, localMetrics, issues);
    if (!RATIO_OPERATORS.has(condition.operator)) {
        issues.push({
            path: `${path}.operator`,
            message: `Operator ${condition.operator} is not supported for ratios`,
        });
    }
    if (!Number.isFinite(condition.value)) {
        issues.push({ path: `${path}.value`, message: 'The ratio value must be finite' });
    }
};
const validateCondition = (condition, path, queryPhase, context, localMetrics, seenRatioIds, issues, negativeContext = false) => {
    switch (condition.kind) {
        case 'predicate': {
            const localMetric = (0, formula_resolution_1.findResolvedLocalMetric)(localMetrics, condition.metric);
            const metric = localMetric?.definition ?? (0, metric_catalog_1.findRealtimeMetric)(condition.metric);
            if (metric === undefined) {
                issues.push({ path: `${path}.metric`, message: `Unknown metric: ${condition.metric}` });
                return;
            }
            if (localMetric === undefined && !isAvailableForValidation(metric, context)) {
                issues.push({ path: `${path}.metric`, message: `The metric is not available yet: ${condition.metric}` });
            }
            validateMetricScope(metric, `${path}.metric`, context, issues);
            if (localMetric !== undefined) {
                validateLocalMetricQueryPhase(localMetric, `${path}.metric`, queryPhase, issues);
            }
            if (!metric.queryPhases.includes(queryPhase)) {
                issues.push({
                    path: `${path}.metric`,
                    message: `The metric cannot be used for ${queryPhase === 'eligibility' ? 'eligibility' : 'scoring'}: ${condition.metric}`,
                });
            }
            if (!metric.supportedOperators.includes(condition.operator)) {
                issues.push({
                    path: `${path}.operator`,
                    message: `Operator ${condition.operator} is not supported for ${condition.metric}`,
                });
            }
            if (!isValueCompatible(metric.valueType, condition.operator, condition.value)) {
                issues.push({ path: `${path}.value`, message: `The value has an invalid type for ${condition.metric}` });
            }
            if (metric.booleanValuePolicy === 'positive_only' && (condition.value !== true || negativeContext)) {
                issues.push({
                    path: `${path}.value`,
                    message: `The metric only supports a positive true predicate: ${condition.metric}`,
                });
            }
            if (condition.operator === 'contains' &&
                metric.textSearchPolicy === 'literal_trigram' &&
                !isSelectiveLiteralTrigramSearch(condition.value)) {
                issues.push({
                    path: `${path}.value`,
                    message: `Contains requires a literal search term of at least 3 characters: ${condition.metric}`,
                });
            }
            if (localMetric?.descriptor.currency !== undefined &&
                isMoneyValue(condition.value) &&
                condition.value.currency !== localMetric.descriptor.currency) {
                issues.push({
                    path: `${path}.value.currency`,
                    message: `The local metric returns ${localMetric.descriptor.currency}: ${condition.metric}`,
                });
            }
            if ((condition.metric === 'company.industry.sni_code' || condition.metric === 'company.industry.sni_prefix') &&
                !hasValidSniValue(condition.value)) {
                issues.push({
                    path: `${path}.value`,
                    message: `The SNI value must be a numeric code with 2 to 5 digits: ${condition.metric}`,
                });
            }
            if (condition.metric === 'company.country.iso3166' && !hasValidIso3166Alpha2Value(condition.value)) {
                issues.push({
                    path: `${path}.value`,
                    message: 'The country code must be uppercase ISO 3166-1 alpha-2: company.country.iso3166',
                });
            }
            return;
        }
        case 'ratio':
            validateRatioCondition(condition, path, queryPhase, context, localMetrics, seenRatioIds, issues);
            return;
        case 'all':
        case 'any':
            if (condition.conditions.length === 0) {
                issues.push({ path: `${path}.conditions`, message: 'A condition group must contain at least one condition' });
            }
            condition.conditions.forEach((child, index) => {
                validateCondition(child, `${path}.conditions[${String(index)}]`, queryPhase, context, localMetrics, seenRatioIds, issues, negativeContext);
            });
            return;
        case 'not':
            validateCondition(condition.condition, `${path}.condition`, queryPhase, context, localMetrics, seenRatioIds, issues, !negativeContext);
            return;
    }
};
/**
 * Validates business constraints after the runtime schema has decoded a
 * definition. This keeps catalog policy out of schema parsing and is pure,
 * deterministic and straightforward to test.
 */
const validateRealtimeScoringDefinition = (definition, context = defaultValidationContext) => {
    const issues = [];
    const seenRuleIds = new Set();
    const seenRatioIds = new Set();
    // Local formulas intentionally permit only number and money inputs, which are
    // available in both current query phases. Resolving declarations once keeps
    // invalid unused formulas and recursive references from being persisted.
    const localMetricResolution = (0, formula_resolution_1.resolveLocalMetrics)(definition, context.hasClientScope, context.allowDeferredMetricReferences);
    issues.push(...localMetricResolution.issues);
    validateCondition(definition.eligibility, 'eligibility', 'eligibility', context, localMetricResolution.resolutions, seenRatioIds, issues);
    const validateComplexity = (condition, path) => {
        const complexity = getConditionComplexity(condition);
        if (complexity.depth > MAX_CONDITION_DEPTH) {
            issues.push({
                path,
                message: `The condition may be at most ${String(MAX_CONDITION_DEPTH)} levels deep`,
            });
        }
        if (complexity.predicateCount > MAX_PREDICATES_PER_CONDITION) {
            issues.push({
                path,
                message: `The condition may contain at most ${String(MAX_PREDICATES_PER_CONDITION)} metrics`,
            });
        }
    };
    validateComplexity(definition.eligibility, 'eligibility');
    if (definition.scoreRules.length > MAX_SCORE_RULES) {
        issues.push({
            path: 'scoreRules',
            message: `A scoring model may contain at most ${String(MAX_SCORE_RULES)} score rules`,
        });
    }
    definition.scoreRules.forEach((rule, index) => {
        if (seenRuleIds.has(rule.id)) {
            issues.push({ path: `scoreRules[${String(index)}].id`, message: `The rule id must be unique: ${rule.id}` });
        }
        seenRuleIds.add(rule.id);
        if (!Number.isFinite(rule.score)) {
            issues.push({
                path: `scoreRules[${String(index)}].score`,
                message: 'The score must be finite',
            });
        }
        validateCondition(rule.when, `scoreRules[${String(index)}].when`, 'scoring', context, localMetricResolution.resolutions, seenRatioIds, issues);
        validateComplexity(rule.when, `scoreRules[${String(index)}].when`);
    });
    return issues;
};
exports.validateRealtimeScoringDefinition = validateRealtimeScoringDefinition;
//# sourceMappingURL=validation.js.map